import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { EMPTY_TOPIC_IDS } from '@helpers';
import { ModalV2 as Modal } from '@utilities';
import { useModalCloseAnimation } from '@utilities/components/ModalV2/utilities/helpers';

import BodyCard from './components/BodyCard';
import HeaderCard from './components/HeaderCard';

import styles from './_index.module.scss';

const PreBuildModal = ({
  activePrompt,
  buildError,
  getPillTooltip,
  getPillValue,
  handleBuild,
  headerIcon,
  isBuildDisabled,
  isBuilding,
  isLoading,
  isModalOpen,
  name,
  prompts,
  routeId,
  routeLabel,
  sameLevelPrompts,
  searchPrompts,
  selectedAnswers,
  setActivePrompt,
  setIsModalOpen,
  setName,
  setSelectedAnswers,
  setTransformedAnswersMap,
  topicIds = EMPTY_TOPIC_IDS,
  transformedAnswersMap,
  transformPromptSelections,
  type,
}) => {
  const { handleClose, isModalClosing } = useModalCloseAnimation({
    isActive: isModalOpen,
    onClose: () => setIsModalOpen(false),
    shouldSlide: true,
  });

  useEffect(() => {
    if (!activePrompt) return;

    if (activePrompt.groupedPrompts) {
      const transformedGroupedAnswers = {};

      activePrompt.groupedPrompts.forEach((groupedPrompt) => {
        const rawAnswers = selectedAnswers?.[groupedPrompt.promptKey] || [];
        try {
          const transformed = transformPromptSelections({
            answers: rawAnswers,
            prompt: groupedPrompt,
          });
          transformedGroupedAnswers[groupedPrompt.promptKey] = transformed;
        } catch (error) {
          console.error(
            `Error transforming prompt selections for grouped prompt ${groupedPrompt.promptKey}:`,
            error
          );
        }
      });

      if (Object.keys(transformedGroupedAnswers).length > 0) {
        setTransformedAnswersMap((prev) => ({
          ...prev,
          ...transformedGroupedAnswers,
        }));
      }

      return;
    }

    const rawAnswers = selectedAnswers[activePrompt.promptKey] || [];

    try {
      const transformed = transformPromptSelections({ answers: rawAnswers, prompt: activePrompt });
      setTransformedAnswersMap((prev) => ({
        ...prev,
        [activePrompt.promptKey]: transformed,
      }));
    } catch (error) {
      console.error('Error transforming prompt selections:', error);
    }
  }, [selectedAnswers, activePrompt, setTransformedAnswersMap, transformPromptSelections]);

  const handleAnswerChange = (newAnswers) => {
    if (!activePrompt) return;
    if (activePrompt.groupedPrompts) {
      setSelectedAnswers((prev) => ({
        ...prev,
        ...newAnswers,
      }));
      return;
    }
    setSelectedAnswers((prev) => ({
      ...prev,
      [activePrompt.promptKey]: newAnswers,
    }));
  };

  return (
    <Modal
      className={styles['prebuild-modal']}
      hasCloseIcon={false}
      isActive={isModalOpen}
      isClosing={isModalClosing}
      shouldSlide
      size="fullscreen"
    >
      <div className={styles['modal-build-survey']}>
        <div className={styles['modal-build-survey-content']}>
          <HeaderCard
            buildError={buildError}
            handleClose={handleClose}
            headerIcon={headerIcon}
            label={routeLabel}
            name={name}
            setName={setName}
          />
          <BodyCard
            activePrompt={activePrompt}
            getPillTooltip={getPillTooltip}
            getPillValue={getPillValue}
            handleBuild={handleBuild}
            handleClose={handleClose}
            isBuildDisabled={isBuildDisabled}
            isBuilding={isBuilding}
            isLoading={isLoading}
            narrativeId={routeId}
            onAnswerChange={handleAnswerChange}
            prompts={prompts}
            sameLevelPrompts={sameLevelPrompts}
            searchPrompts={searchPrompts}
            selectedAnswers={selectedAnswers}
            setActivePrompt={setActivePrompt}
            setSelectedAnswers={setSelectedAnswers}
            topicIds={topicIds}
            transformedAnswersMap={transformedAnswersMap}
            type={type}
          />
        </div>
      </div>
    </Modal>
  );
};

PreBuildModal.propTypes = {
  activePrompt: PropTypes.object,
  buildError: PropTypes.string,
  getPillTooltip: PropTypes.func,
  getPillValue: PropTypes.func,
  handleBuild: PropTypes.func.isRequired,
  headerIcon: PropTypes.node,
  isBuildDisabled: PropTypes.bool.isRequired,
  isBuilding: PropTypes.bool,
  isLoading: PropTypes.bool,
  isModalOpen: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  prompts: PropTypes.array.isRequired,
  routeId: PropTypes.string,
  routeLabel: PropTypes.string,
  sameLevelPrompts: PropTypes.array,
  searchPrompts: PropTypes.array,
  selectedAnswers: PropTypes.object.isRequired,
  setActivePrompt: PropTypes.func.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
  setName: PropTypes.func.isRequired,
  setSelectedAnswers: PropTypes.func.isRequired,
  setTransformedAnswersMap: PropTypes.func.isRequired,
  topicIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.oneOf([null])])
  ),
  transformedAnswersMap: PropTypes.object.isRequired,
  transformPromptSelections: PropTypes.func.isRequired,
  type: PropTypes.string,
};

export default PreBuildModal;
